import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { DateTime } from 'luxon';
import { Params, Router, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { NodeAction, NodeComponent, NodePosition } from '../node/node.component';
import { ItemSubicon, SUBICONS_ALL } from '@app/components/item/icon/subicons/item-subicons.component';
import { DraftWarningComponent } from '@app/components/shared/draft-warning/draft-warning.component';
import { CostComponent } from '@app/components/util/cost/cost.component';
import { DateComponent } from '@app/components/util/date/date.component';
import { TreeHelper } from '@app/helpers/tree-helper';
import { CostHelper } from '@app/helpers/cost-helper';
import { NodeHelper } from '@app/helpers/node-helper';
import { EventService } from '@app/services/event.service';
import { StorageService } from '@app/services/storage.service';
import { NodeService } from '@app/services/node.service';
import { CurrencyService } from '@app/services/currency.service';
import { DebugService } from '@app/services/debug.service';
import { SpiritTreeRenderService } from '@app/services/spirit-tree-render.service';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import { cancellableEvent, noInputs } from '@app/rxjs/operators';
import { INode, ISpiritTree, ISpiritTreeTier, ICost, IItem } from 'skygame-data';

export interface SpiritTreeNodeClickEvent {
  node: INode;
  event: MouseEvent;
}

interface TreeRow {
  left?: INode;
  center?: INode;
  right?: INode;
  /** True for the row bordering the tier below it, so a separator renders next to it. */
  tierStart?: boolean;
}

/**
 * Editor affordance rendered in a slot next to the node being edited: a ghost tile
 * on a free direction, or a chip on an occupied one to move or drop its connection.
 */
export type TreeEditSlotKind = 'add' | 'link' | 'unlink';

export interface TreeEditSlot {
  position: NodePosition;
  /** Level within the column, root = 0. -1 is the slot below the root. */
  level: number;
  kind: TreeEditSlotKind;
  /** Direction relative to the node being edited, echoed back on click. */
  direction: 'nw' | 'n' | 'ne' | 'below';
}

interface TreeRenderSlot {
  node?: INode;
  edit?: TreeEditSlot;
}

interface TreeRenderColumn {
  position: NodePosition;
  slots: ReadonlyArray<TreeRenderSlot>;
  /**
   * An extra slot below the root row, rendered first so column-reverse puts it at the
   * bottom. Every column reserves it so the columns keep their relative alignment;
   * only the centre one carries the ghost tile.
   */
  hasBelow: boolean;
  below?: TreeEditSlot;
}

interface TreeColumns {
  left: (INode | undefined)[];
  center: (INode | undefined)[];
  right: (INode | undefined)[];
}

type ShareMode = 'share' | 'clipboard';

/**
 * Page-wide node action shared by every tree on the page. Toggling the action
 * on one tree's control bar (or via the `u` / `n` keyboard shortcuts) switches
 * them all at once — matching the legacy `signalAction` behaviour.
 */
const sharedNodeAction = signal<NodeAction>('unlock');

/**
 * Spirit-tree renderer. Lays the tree out as a vertical stack of 3-column
 * rows, delegating each node to `NodeComponent`.
 *
 * Owns the full legacy `SpiritTreeComponent` behaviour: the unlock/navigate
 * click pipeline, the in-widget control bar (action toggle + unlock-all / image
 * export / edit menu) behind `enableControls`, and the page-wide `u` / `n`
 * keyboard shortcuts.
 */
@Component({
  selector: 'app-spirit-tree',
  templateUrl: './spirit-tree.component.html',
  styleUrl: './spirit-tree.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NodeComponent, CostComponent, DateComponent, MatIcon, RouterLink, DraftWarningComponent, NgTemplateOutlet, TooltipDirective]
})
export class SpiritTreeComponent {
  readonly tree = input.required<ISpiritTree>();
  readonly name = input<string | undefined>(undefined);
  /** Optional router link for the tree name, e.g. the spirit page this tree belongs to. */
  readonly nameLink = input<string | unknown[] | undefined>(undefined);
  /** Query params for `nameLink`, e.g. `{ highlightTree: tree.guid }`. */
  readonly nameQueryParams = input<Params | undefined>(undefined);
  /** Optional badge rendered next to the name, e.g. 'Guide'. */
  readonly nameTag = input<string | undefined>(undefined);
  readonly highlight = input<boolean>(false);
  readonly highlightItem = input<string | ReadonlyArray<string> | undefined>(undefined);
  readonly highlightNode = input<string | ReadonlyArray<string> | undefined>(undefined);
  readonly enableControls = input<boolean>(true);
  readonly showNodeTooltips = input<boolean>(true);
  readonly opaqueNodes = input<boolean | ReadonlyArray<string> | undefined>(undefined);
  readonly padBottom = input<boolean>(false);
  readonly forceNodeAction = input<NodeAction | undefined>(undefined);
  readonly nodeOverlayTemplate = input<TemplateRef<unknown> | undefined>(undefined);
  /** Subicons drawn on every node; narrow it where a marker is redundant for the host page. */
  readonly nodeSubIcons = input<ReadonlyArray<ItemSubicon>>(SUBICONS_ALL);
  /** Editor-only affordances placed in the tree layout; empty for a read-only tree. */
  readonly editSlots = input<ReadonlyArray<TreeEditSlot>>([]);

  readonly nodeClicked = output<SpiritTreeNodeClickEvent>();
  readonly editSlotClicked = output<TreeEditSlot>();

  private readonly _elementRef = inject(ElementRef);
  private readonly _router = inject(Router);
  private readonly _eventService = inject(EventService);
  private readonly _storageService = inject(StorageService);
  private readonly _nodeService = inject(NodeService);
  private readonly _currencyService = inject(CurrencyService);
  private readonly _debugService = inject(DebugService);
  private readonly _renderService = inject(SpiritTreeRenderService);

  /** Bumped on `itemToggled` so cost computeds re-run after in-place unlock mutations. */
  private readonly _refresh = signal(0);

  /** Whether the overflow menu (image export / edit) is expanded. */
  readonly showMenu = signal(false);

  constructor() {
    this._eventService.itemToggled.pipe(takeUntilDestroyed()).subscribe(() => {
      this._refresh.update(v => v + 1);
    });

    // Close the more-actions dropdown when clicking outside the menu container.
    this._eventService.clicked.pipe(takeUntilDestroyed()).subscribe(event => {
      if (!this.showMenu()) { return; }
      const menu = (this._elementRef.nativeElement as HTMLElement).querySelector('.atmos-spirit-tree__menu');
      if (!menu || !menu.contains(event.target as Node)) {
        this.showMenu.set(false);
      }
    });

    // Page-wide u / n / f shortcuts. Skipped for forced-action (emit) trees and
    // when controls are disabled, mirroring legacy.
    this._eventService.keydown.pipe(takeUntilDestroyed(), cancellableEvent(), noInputs()).subscribe(evt => {
      if (this.forceNodeAction() || !this.enableControls()) { return; }
      if (evt.shiftKey || evt.ctrlKey || evt.altKey || evt.metaKey) { return; }

      let action: NodeAction | undefined;
      switch (evt.key?.toLocaleLowerCase()) {
        case 'n': action = 'navigate'; break;
        case 'u': action = 'unlock'; break;
        case 'f': action = 'favourite'; break;
        default: return;
      }

      if (sharedNodeAction() === action) { action = 'unlock'; }
      sharedNodeAction.set(action);
      evt.preventDefault();
    });
  }

  readonly visibleName = computed<string | undefined>(() => {
    const t = this.tree();
    return this.name()
      ?? t.name
      ?? t.eventInstanceSpirit?.name
      ?? t.eventInstanceSpirit?.spirit?.name
      ?? t.travelingSpirit?.spirit?.name
      ?? t.specialVisitSpirit?.spirit?.name
      ?? t.spirit?.name;
  });

  readonly rows = computed<ReadonlyArray<TreeRow>>(() => {
    const t = this.tree();
    const rows: TreeRow[] = [];

    if (t.node) {
      // Walk the node chain bottom (root) → top.
      const buildRows = (n: INode, dir: number, level: number) => {
        const row = rows[level] ?? (rows[level] = {});
        if (dir < 0) { row.left = n; }
        else if (dir > 0) { row.right = n; }
        else { row.center = n; }
        if (n.nw) { buildRows(n.nw, dir - 1, level); }
        if (n.ne) { buildRows(n.ne, dir + 1, level); }
        if (n.n) { buildRows(n.n, dir, level + 1); }
      };
      buildRows(t.node, 0, 0);
      // Reverse so the root is at the bottom visually.
      return rows.slice().reverse();
    } else if (t.tier) {
      let level = -1;
      const tiers = TreeHelper.getTiers(t);
      tiers.forEach((tier, tierIndex) => {
        tier.rows.forEach((tierRow, rowIndex) => {
          level++;
          const row: TreeRow = {};
          row.left = tierRow[0] ?? undefined;
          row.center = tierRow[1] ?? undefined;
          row.right = tierRow[2] ?? undefined;
          if (rowIndex === 0 && tierIndex > 0) { row.tierStart = true; }
          rows[level] = row;
        });
      });
      return rows.slice().reverse();
    }

    return rows;
  });

  /**
   * Columns for node trees: left / center / right arrays indexed by level (0 = root).
   * CSS `flex-direction: column-reverse` places root at the bottom visually.
   */
  readonly columns = computed<TreeColumns>(() => {
    const cols: TreeColumns = { left: [], center: [], right: [] };
    const t = this.tree();
    if (!t.node) { return cols; }

    const build = (n: INode, dir: number, level: number) => {
      const arr = dir < 0 ? cols.left : dir > 0 ? cols.right : cols.center;
      arr[level] = n;
      if (n.nw) { build(n.nw, dir - 1, level); }
      if (n.ne) { build(n.ne, dir + 1, level); }
      if (n.n)  { build(n.n,  dir,     level + 1); }
    };
    build(t.node, 0, 0);

    // Trim trailing empty slots at the top.
    const trimTop = (arr: Array<INode | undefined>) => {
      while (arr.length && arr[arr.length - 1] === undefined) { arr.pop(); }
    };
    trimTop(cols.left);
    trimTop(cols.center);
    trimTop(cols.right);

    return cols;
  });

  /**
   * Columns in DOM order. The centre trunk comes first so tab order runs
   * middle → left → right; CSS `order` restores the visual left/centre/right
   * arrangement.
   */
  readonly orderedColumns = computed<ReadonlyArray<{ position: NodePosition; nodes: ReadonlyArray<INode | undefined> }>>(() => {
    const c = this.columns();
    return [
      { position: 'center' as const, nodes: c.center },
      { position: 'left' as const, nodes: c.left },
      { position: 'right' as const, nodes: c.right }
    ];
  });

  /**
   * Columns paired with their edit slots. Columns are padded up to the highest edit
   * level so a ghost tile above the topmost node still gets a slot to live in.
   */
  readonly renderColumns = computed<ReadonlyArray<TreeRenderColumn>>(() => {
    const slots = this.editSlots();
    const byPosition = new Map<string, TreeEditSlot>();
    slots.forEach(s => byPosition.set(`${s.position}:${s.level}`, s));

    return this.orderedColumns().map(col => {
      const maxEditLevel = slots.reduce((max, s) => s.position === col.position ? Math.max(max, s.level) : max, -1);
      const length = Math.max(col.nodes.length, maxEditLevel + 1);
      const rendered: Array<TreeRenderSlot> = [];
      for (let level = 0; level < length; level++) {
        rendered.push({ node: col.nodes[level], edit: byPosition.get(`${col.position}:${level}`) });
      }
      const hasBelow = slots.some(s => s.level === -1);
      return { position: col.position, slots: rendered, hasBelow, below: byPosition.get(`${col.position}:-1`) };
    });
  });

  onEditSlotClicked(slot: TreeEditSlot, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.editSlotClicked.emit(slot);
  }

  editSlotTitle(slot: TreeEditSlot): string {
    switch (slot.kind) {
      case 'link': return 'Move this connection to the selected node';
      case 'unlink': return slot.direction === 'below'
        ? 'Disconnect here and make the selected node the root'
        : 'Remove this connection and everything beyond it';
      default: return 'Add a node here';
    }
  }

  readonly action = computed<NodeAction>(() => this.forceNodeAction() ?? sharedNodeAction());

  /** Active node action, exposed for the control bar's active-state styling. */
  readonly currentAction = this.action;

  /** Whether the in-widget control bar should render. Hidden for forced-action trees. */
  readonly showControls = computed<boolean>(() => this.enableControls() && !this.forceNodeAction());

  readonly eventDate = computed<DateTime | undefined>(() => {
    const t = this.tree();
    return t.travelingSpirit?.date ?? t.specialVisitSpirit?.visit?.date;
  });

  /** True while any item in the tree is still locked (drives the unlock-all label). */
  readonly hasLocked = computed<boolean>(() => {
    this._refresh();
    return TreeHelper.getNodes(this.tree()).some(n => n.item && !n.item.unlocked);
  });

  private readonly opaqueMap = computed<Set<string>>(() => {
    const o = this.opaqueNodes();
    const set = new Set<string>();
    if (Array.isArray(o)) { o.forEach(g => set.add(g)); }
    return set;
  });

  readonly opaqueAll = computed<boolean>(() => this.opaqueNodes() === true);

  private readonly highlightItemSet = computed<Set<string>>(() => {
    const h = this.highlightItem();
    const set = new Set<string>();
    if (typeof h === 'string') { set.add(h); }
    else if (Array.isArray(h)) { h.forEach(g => set.add(g)); }
    return set;
  });

  private readonly highlightNodeSet = computed<Set<string>>(() => {
    const h = this.highlightNode();
    const set = new Set<string>();
    if (typeof h === 'string') { set.add(h); }
    else if (Array.isArray(h)) { h.forEach(g => set.add(g)); }
    return set;
  });

  readonly totalCost = computed<ICost>(() => {
    return CostHelper.add(CostHelper.create(), ...TreeHelper.getNodes(this.tree()));
  });

  readonly remainingCost = computed<ICost>(() => {
    this._refresh();
    const locked = TreeHelper.getNodes(this.tree()).filter(n => !n.unlocked && !n.item?.unlocked);
    return CostHelper.add(CostHelper.create(), ...locked);
  });

  isNodeOpaque(node: INode): boolean {
    return this.opaqueAll() || this.opaqueMap().has(node.guid);
  }

  isNodeHighlighted(node: INode): boolean {
    if (this.highlightNodeSet().has(node.guid)) { return true; }
    if (node.item && this.highlightItemSet().has(node.item.guid)) { return true; }
    return false;
  }

  onNodeClicked(event: MouseEvent, node: INode): void {
    // The node tile only emits plain left-clicks; modifier / middle clicks are
    // left to the browser so the item opens in a new tab via the anchor.

    // In emit mode the parent owns the behavior (calculators, friend trees, etc.).
    if (this.action() === 'emit') {
      this.nodeClicked.emit({ node, event });
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (!node.item) { return; }

    switch (this.action()) {
      case 'unlock': this.toggleNode(node); break;
      case 'navigate': this.findNode(node); break;
      case 'favourite': this.toggleFavourite(node); break;
    }

    // Cancel the anchor's native navigation — we handled the click ourselves.
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  private findNode(node: INode): void {
    void this._router.navigate(['/item', node.item!.guid]);
  }

  /** Mirrors the item page's toggle: the subicon star redraws on `itemFavourited`. */
  private toggleFavourite(node: INode): void {
    const item = node.item!;
    item.favourited = !item.favourited;
    item.favourited
      ? this._storageService.addFavourites(item.guid)
      : this._storageService.removeFavourites(item.guid);
    this._eventService.itemFavourited.next(item);
  }

  private toggleNode(node: INode): void {
    const tree = this.tree();
    const item = node.item!;
    const unlock = !item.unlocked;

    if (unlock && (item.group === 'SeasonPass' || item.group === 'Ultimate')) {
      const isFirstNode = node === item.nodes?.at(0);
      const season = item.season;
      if (isFirstNode && season && !this._storageService.hasSeasonPass(season.guid)) {
        const ok = confirm(
          `You've selected an item that requires the ${season.name} season pass. Do you want to unlock this item and the season pass?`
        );
        if (!ok) { return; }
        this._storageService.addSeasonPasses(season.guid);
      }
    }

    let toggleConnected = this._storageService.getKey('tree.unlock-connected') !== '0';
    if (!unlock && !node.unlocked) { toggleConnected = false; }

    const unlockCost = CostHelper.create();
    if (unlock) {
      const nodesToUnlock = toggleConnected ? NodeHelper.trace(node) : [node];
      for (const n of nodesToUnlock) {
        if (n.item && !n.item.unlocked) {
          this._nodeService.unlock(n);
          CostHelper.add(unlockCost, n);
        }
      }
    } else {
      const nodesToLock = toggleConnected ? NodeHelper.all(node) : [node];
      for (const n of nodesToLock) {
        if (n === node || n.unlocked) {
          this._nodeService.lock(n);
          CostHelper.add(unlockCost, n);
        }
      }
    }

    if (unlock) { CostHelper.invert(unlockCost); }
    this._currencyService.addTreeCost(unlockCost, tree);
  }

  setAction(action: NodeAction): void {
    sharedNodeAction.set(action);
  }

  toggleMenu(): void {
    this.showMenu.update(v => !v);
  }

  /** Unlock (or lock) every item in the tree at once. */
  unlockAll(): void {
    const tree = this.tree();
    // Debug helper: copy tree GUID instead of mutating.
    if (this._debugService.copyTree) {
      void navigator.clipboard.writeText(tree.guid || '');
      return;
    }

    const itemNodes = TreeHelper.getNodes(tree).filter(n => n.item);
    const items: Array<IItem> = itemNodes.map(n => n.item!);
    const shouldUnlock = items.filter(v => !v.unlocked).length;

    const msg = `Are you sure you want to ${shouldUnlock ? 'UNLOCK' : 'REMOVE'} all items from this tree?`;
    if (!confirm(msg)) { return; }

    const unlockCost = CostHelper.create();
    if (shouldUnlock) {
      itemNodes.filter(n => !n.item!.unlocked).forEach(node => {
        CostHelper.add(unlockCost, node);
        node.item!.unlocked = true;
        node.unlocked = true;
        this._storageService.addUnlocked(node.item!.guid);
        this._storageService.addUnlocked(node.guid);
        this._eventService.itemToggled.next(node.item!);
      });
    } else {
      itemNodes.filter(n => n.item!.unlocked).forEach(node => {
        if (node.unlocked) { CostHelper.add(unlockCost, node); }
        node.item!.unlocked = false;
        const refNodes = node.item!.nodes || [];
        refNodes.forEach(n => n.unlocked = false);
        this._storageService.removeUnlocked(node.item!.guid);
        this._storageService.removeUnlocked(...refNodes.map(n => n.guid));
        this._eventService.itemToggled.next(node.item!);
      });
    }

    if (shouldUnlock) { CostHelper.invert(unlockCost); }
    // TODO: this does not track the cost when locking nodes outside of this tree.
    this._currencyService.addTreeCost(unlockCost, tree);
  }

  editTree(): void {
    this.showMenu.set(false);
    const tree = this.tree();
    const result = confirm('Do you want to clone this tree as a new tree? [Yes] Clone [No] Modify');
    void this._router.navigate(['/spirit-tree/editor'], { queryParams: { tree: tree.guid, modify: !result } });
  }

  async exportPng(mode: ShareMode, tooltip?: TooltipDirective): Promise<void> {
    // Copy keeps the menu open — its "Copied!" tooltip hangs off the menu item.
    if (mode !== 'clipboard') { this.showMenu.set(false); }
    const tree = this.tree();
    if (mode === 'share' && !navigator.share) { alert('Sharing is not supported by this browser.'); return; }
    if (mode === 'clipboard' && typeof ClipboardItem === 'undefined') {
      alert('Copying to clipboard is not supported by this browser.');
      return;
    }

    try {
      const title = tree.spirit?.name
        ?? tree.eventInstanceSpirit?.spirit?.name
        ?? tree.travelingSpirit?.spirit?.name
        ?? tree.specialVisitSpirit?.spirit?.name;
      let subtitle: string | undefined = this.visibleName();
      const date = this.eventDate();
      if (date) {
        subtitle = tree.travelingSpirit
          ? `TS #${tree.travelingSpirit.number}`
          : tree.specialVisitSpirit
            ? `${tree.specialVisitSpirit.visit.name}`
            : '';
        subtitle += ` (${date.toFormat('dd-MM-yyyy')})`;
      } else if (subtitle === title || subtitle === 'Spirit tree') {
        subtitle = undefined;
      }

      const canvas = await this._renderService.render(tree, { title, subtitle, background: true });
      if (mode === 'share') {
        this._renderService.shareCanvas(canvas, 'spirit-tree.png');
      } else {
        await this._renderService.copyCanvas(canvas);
        tooltip?.open();
      }
    } catch (e: any) {
      alert(`Failed to copy the spirit tree: ${e?.message ?? e}`);
    }
  }
}
